import React, {useEffect, useState} from "react";
import Container from "@material-ui/core/Container";
import {
    Card,
    Grid,
    Box,
    Typography,
    Button,
    withStyles
} from "@material-ui/core";
import Navigation from "../../Components/Navigation";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../redux/store";
import {makeStyles, useTheme} from "@material-ui/core/styles";
import {colorInputBorder, colorSecondary, colorWhite} from "../../Constants/theme";
import {bundleOptions} from "../../Constants/credits";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {createCredit, updateCreditType} from "../../redux/credits/credits.actions";
import {useHistory} from "react-router";
import moment from "moment/moment";
import withLocation from "../../hocs/withLocation";
import {statesWithoutPrescription} from "../../Constants/locations";
import {api_getCreditPrices, api_getCreditRequestsCountByType} from "../../redux/credits/credits.api";
import API from "../../helpers/apiHelpers";

interface Props {
    isOnBoarding: boolean;
    setIsOnBoarding: React.Dispatch<React.SetStateAction<boolean>>;
    getLocation: () => void;
}

const boxShadow = "0px 0.5px 4px rgba(0, 0, 0, 0.12), 0px 6px 13px rgba(0, 0, 0, 0.12)";
const useStyle = makeStyles((theme) => ({
    wrapper: {
        padding: "1.5em"
    },
    title: {
        marginBottom: 25
    },
    creditsCount: {
        color: colorSecondary,
        fontWeight: "bold",
        fontSize: "45px",
        lineHeight: "inherit",
    },
    textAccent: {
        color: colorSecondary,
        fontWeight: "bold"
    },
    textPlain: {
        letterSpacing: "-0.078px",
        fontSize: "19px"
    },
    creditsSliderCard: {
        marginTop: "15px",
        marginBottom: "40px",
        padding: "25px 10px",
        background: "#F9F9F9",
        border: "1px solid #F2F2F2",
        borderRadius: "5px",
        [theme.breakpoints.down("xs")]: {
            marginBottom: "10px"
        }
    },
    checkboxLabel: {
        color: colorInputBorder
    },
    buttons: {
        width: "158px",
        height: "48px",
        marginBottom: 10,
        marginLeft: 10,
        lineHeight: "16px",
    },
}));
// const CreditsSlider = withStyles({
// 	root: {
// 		color: colorSecondary,
// 	},
// 	thumb: {
// 		height: 28,
// 		width: 28,
// 		backgroundColor: colorWhite,
// 		marginTop: -12,
// 		marginLeft: -12,
// 		boxShadow: boxShadow,
// 		"&:focus, &:hover, &$active": {
// 			boxShadow: boxShadow,
// 			// Reset on touch devices, it doesn't add specificity
// 			"@media (hover: none)": {
// 				boxShadow: boxShadow,
// 			},
// 		},
// 	},
// 	track: {
// 		height: 4,
// 		borderRadius: 4,
// 	},
// 	rail: {
// 		height: 4,
// 		borderRadius: 4,
// 		backgroundColor: "rgba(60, 60, 67, 0.4)",
// 	},
// 	mark: {
// 		display: "none"
// 	}
// })(Slider);

const PurchaseCredits = ({isOnBoarding, setIsOnBoarding, getLocation}: Props) => {
    const theme = useTheme();
    const classes = useStyle();
    const dispatch = useAppDispatch();
    const [creditsAmount, setCreditsAmount] = useState(2);
    const creditAmountWithNames = {
        "ADVICE": {
            2: "TWO_ADVICE_CREDITS",
            4: "FOUR_ADVICE_CREDITS",
            6: "SIX_ADVICE_CREDITS",
            12: "TWELVE_ADVICE_CREDITS",
        },
        "DEFAULT": {
            2: "TWO_CREDITS",
            4: "FOUR_CREDITS",
            6: "SIX_CREDITS",
            12: "TWELVE_CREDITS",
        },
    };
    const [purchasePrice, setPurchasePrice] = useState(56);
    const [discount, setDiscount] = useState(20);
    const [prices, setPrices] = useState([]);
    const [creditsInfoAdvice, setCreditsInfoAdvice] = useState([]);
    const [adviceCreditsAvailable, setAdviceCreditsAvailable] = useState(0);
    const [defaultCreditsAvailable, setDefaultCreditsAvailable] = useState(0);
    const [isTelehealthOnly, setIsTelehealthOnly] = useState(true);
    const creditType = useSelector((state: RootState) => state?.credits.creditType);
    const creditsAvailable = useSelector((state: RootState) => state?.credits.creditsAmount);
    const expirationDate = useSelector((state: RootState) => state?.credits.expirationDate);
    let isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const location = useSelector((state: RootState) => state?.common.location);
    const clientPrices = useSelector((state: RootState) => state?.common?.prices);
    const isWhiteLabel = useSelector((state: RootState) => state?.branding.partnerPreferencesOn);
    const user = useSelector((state: RootState) => state?.user);
    const profile = useSelector((state: RootState) => state?.userProfile);
    const history = useHistory();
    const isAdvice = creditType === "ADVICE";

    useEffect(() => {
        if (location === undefined) {
            getLocation();
        }
    }, []);

    useEffect( () => { (async () => {
        api_getCreditRequestsCountByType("ADVICE")
            .then((count) => setAdviceCreditsAvailable(count));
        api_getCreditRequestsCountByType("DEFAULT")
            .then((count) => setDefaultCreditsAvailable(count));
        api_getCreditPrices(location, ["ADVICE", "DEFAULT"])
            .then((prices) => setCreditsInfoAdvice(prices));
        setPrices([clientPrices.adviceVideo, clientPrices.rxVideo]);
    })()}, [isTelehealthOnly]);

    useEffect(() => {
        let isTelehealthOnly = !location ? true : statesWithoutPrescription.find(state => state.value === location);
        if (isTelehealthOnly) {
            setIsTelehealthOnly(true);
        } else {
            setIsTelehealthOnly(false);
        }
    }, [location, profile]);

    const handlePurchase = () => {
        const purchaseInfo = {
            clientUserId: user.userId,
            creditPrice: bundleOptions[creditType].find(option => option.value === creditsAmount).name,
            paymentRequest: {
                amount: `USD ${purchasePrice}`,
                type: "square"
            },
            creditRequestType: creditType,
        };
        dispatch(createCredit(purchaseInfo));
        history.push("/invoice?type=credits");
    };

    useEffect(() => {
        const data = creditsInfoAdvice.find(el => el.creditType === creditAmountWithNames[creditType][creditsAmount]);
        setPurchasePrice(data?.price.split(" ")[1].split(".")[0]);
        setDiscount( data?.discountAmount );
    }, [creditsAmount, creditType, creditsInfoAdvice]);

    return (
        <Container maxWidth="md">
            <Box marginTop="115px" marginBottom="75px">
                <Grid container>
                    {!isMobile &&
                    <Grid item sm={3} xs={12}>
                        <Navigation/>
                    </Grid>
                    }
                    <Grid item md={9} xs={12}>
                        <Card variant="outlined">
                            <Grid item xs={12} className={classes.wrapper}>
                                <Grid container>
                                    <Grid item xs={12} className={classes.title}>
                                        <Typography variant="h2">
                                            Appointment Bundle Options
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            type="button"
                                            onClick={() => dispatch(updateCreditType("ADVICE"))}
                                            color={isAdvice ? "secondary" : "default"}
                                            variant="contained"
                                            className={classes.buttons}
                                        >
                                            Advice
                                        </Button>
                                        {
                                            !isWhiteLabel && !isTelehealthOnly
                                            &&
                                            <Button
                                                type="button"
                                                onClick={() => dispatch(updateCreditType("DEFAULT"))}
                                                color={isAdvice ? "default" : "secondary"}
                                                variant="contained"
                                                className={classes.buttons}
                                            >
                                                Advice +
                                                Medications
                                            </Button>
                                        }
                                    </Grid>
                                    <Grid container item sm={6} xs={12} className={classes.creditsSliderCard}>
                                        <Grid item container xs={12} style={{marginBottom: "20px"}}>
                                            {bundleOptions[creditType].map(item => {
                                                return (
                                                    <Grid item xs={3} key={item.name}>
                                                        <Button
                                                            type="button"
                                                            onClick={() => setCreditsAmount(item.value)}
                                                            variant="contained"
                                                            color={creditsAmount === item.value ? "primary" : "default"}
                                                            style={{fontSize: 24}}
                                                        >
                                                            {item.value.toString()}
                                                        </Button>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box marginBottom="10px">
                                                <Typography variant="h3">
                                                    Bundles to Purchase
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography color="textSecondary" className={classes.textPlain}>
                                                {
                                                    creditsAmount && expirationDate ?
                                                        `Renew expiration date to ${moment().add(365, "days").format("l")}`
                                                        :
                                                        `Bundles expire ${moment().add(365, "days").format("l")} if unused`
                                                }
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid container item sm={6} xs={12} style={isMobile ? {} : {paddingLeft: "20px"}}>
                                        <Grid item xs={12}>
                                            <Box marginTop="33px" marginBottom="15px">
                                                <Typography className={classes.creditsCount}>
                                                    ${purchasePrice}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box marginBottom="10px">
                                                <Typography variant="h3">
                                                    One-Time Purchase Price
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} style={{marginBottom: "20px"}}>
                                            <Button
                                                type="button"
                                                variant="contained"
                                                color="secondary"
                                                onClick={handlePurchase}
                                            >
                                                Purchase Bundle For ${purchasePrice}
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box marginBottom="40px">
                                                <Typography color="textSecondary" className={classes.textPlain}>
                                                    Save
                                                    <span className={classes.textAccent}> {discount}% </span>
                                                    per appointment
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                    <Grid container item sm={6} xs={12}>
                                        <Grid item xs={12}>
                                            <Box marginBottom="15px">
                                                <Typography className={classes.creditsCount}>
                                                    {isAdvice? adviceCreditsAvailable : defaultCreditsAvailable}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box marginBottom="15px">
                                                <Typography variant="h3">
                                                    Available Appointments
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box marginBottom="50px">
                                                {
                                                    creditsAmount && expirationDate ?
                                                        <Typography color="textSecondary" className={classes.textPlain}>
                                                            Credits expire {moment(expirationDate).format("l")} if
                                                            unused
                                                        </Typography>
                                                        :
                                                        <Typography color="textSecondary" className={classes.textPlain}>
                                                            Save up to
                                                            <span className={classes.textAccent}> 24% </span>
                                                            on appointments!
                                                        </Typography>
                                                }
                                            </Box>
                                        </Grid>
                                    </Grid>
                                    <Grid container item sm={6} xs={12} style={isMobile ? {} : {paddingLeft: "20px"}}>
                                        <Grid item xs={12}>
                                            <Box marginBottom="15px">
                                                <Typography className={classes.creditsCount}>
                                                    ${isAdvice ? prices[0] : prices[1]}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box marginBottom="15px">
                                                <Typography variant="h3">
                                                    Single Appointment Cost
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box marginBottom="50px">
                                                <Typography color="textSecondary" className={classes.textPlain}>
                                                    For appointments without a bundle
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                    {isAdvice && <Typography color="textSecondary">Note: advice appointments cannot result in prescriptions.</Typography>}
                                </Grid>
                                {isOnBoarding &&
                                <Grid item container xs={12} alignItems="center" alignContent="center" justify="center">
                                    <Grid item xs={6} style={{alignSelf: "center"}}>
                                        <Button
                                            type="button"
                                            variant="text"
                                            color="primary"
                                            onClick={() => {
                                                if (isOnBoarding) {
                                                    setIsOnBoarding(false);
                                                    document.cookie = "isOnBoarding= ; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
                                                }
                                                history.push("/schedule");
                                            }}
                                        >
                                            I don’t want to purchase a bundle at this time
                                        </Button>
                                    </Grid>
                                </Grid>
                                }
                            </Grid>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default withLocation(PurchaseCredits);
